<template>
  <section class="calculation-page">
    <div class="page-header">
      <p class="eyebrow">Phase 1 Tooling</p>
      <h2>Calculation &amp; Trace Inspector</h2>
      <p>
        Run an ad-hoc `run_sizing_calculation` for any saved scenario, inspect
        the scenario context before calculating, and look up persisted
        calculation traces by trace ID for audit and triage.
      </p>
    </div>

    <Card>
      <template #title>Run Sizing Calculation</template>
      <template #content>
        <div class="form-grid">
          <label>
            Scenario ID
            <InputText v-model="scenarioId" placeholder="scenario uuid" />
            <small>
              From `/sizings/:sizingId/scenarios/:scenarioId/...`, use the ID
              after `/scenarios/`.
            </small>
          </label>
          <label>
            Calculation Mode
            <Dropdown v-model="calculationMode" :options="calculationModes" />
            <small>
              `baseline_existing_equipment` verifies the saved equipment;
              `parity_fixture` matches the parity workflows.
            </small>
          </label>
          <label class="toggle-label">
            Debug Summary
            <InputSwitch v-model="debug" />
            <small
              >Includes per-pump stage diagnostics and effective
              thresholds.</small
            >
          </label>
          <label class="toggle-label">
            Persist Trace
            <InputSwitch v-model="persistTrace" />
            <small
              >Disable to run without writing a durable calculation
              trace.</small
            >
          </label>
        </div>
        <div class="action-row mt-3">
          <Button
            label="Inspect Context"
            class="p-button-outlined"
            :loading="isInspecting"
            :disabled="!scenarioId"
            @click="inspectContext"
          />
          <Button
            label="Run Calculation"
            class="p-button-success"
            :loading="isCalculating"
            :disabled="!scenarioId"
            @click="runCalculation"
          />
        </div>
      </template>
    </Card>

    <div v-if="error" class="error-banner">{{ error }}</div>

    <Card v-if="contextSummary" class="mt-4">
      <template #title>Scenario Context Pre-Flight</template>
      <template #subtitle>
        Inspect required inputs before spending a calculation run.
      </template>
      <template #content>
        <div class="context-grid">
          <div
            v-for="entry in contextSummary.counts"
            :key="entry.label"
            class="context-chip"
            :class="{ empty: entry.count === 0 && entry.required }"
          >
            {{ entry.label }}: {{ entry.count }}
          </div>
        </div>
        <Panel
          v-if="contextSummary.missingData.length"
          header="Missing Context Data"
          class="mt-3"
        >
          <ul>
            <li v-for="item in contextSummary.missingData" :key="item">
              <code>{{ item }}</code>
            </li>
          </ul>
        </Panel>
        <p v-else class="muted-copy mt-3">No missing context data reported.</p>
      </template>
    </Card>

    <Card v-if="calculationResult" class="mt-4">
      <template #title>Calculation Result</template>
      <template #subtitle>
        <Tag
          :value="calculationResult.status"
          :severity="statusSeverity(calculationResult.status)"
        />
      </template>
      <template #content>
        <div class="provenance-grid">
          <div>
            <strong>Trace ID:</strong>
            <code>{{ calculationResult.traceId }}</code>
            <Button
              label="Copy"
              class="p-button-text p-button-sm"
              @click="copyText(calculationResult.traceId)"
            />
            <Button
              label="Load Trace"
              class="p-button-text p-button-sm"
              @click="loadTrace(calculationResult.traceId)"
            />
          </div>
          <div>
            <strong>Engine:</strong> {{ calculationResult.engineVersion }}
          </div>
          <div>
            <strong>Engine Hash:</strong>
            <code class="hash">{{ calculationResult.engineHash }}</code>
          </div>
          <div>
            <strong>Input Hash:</strong>
            <code class="hash">{{ calculationResult.inputHash }}</code>
          </div>
          <div>
            <strong>Elapsed:</strong> {{ calculationResult.elapsedMs }} ms
          </div>
        </div>

        <DataTable
          :value="metricRows"
          responsive-layout="scroll"
          size="small"
          class="mt-3"
        >
          <template #empty>
            <p class="text-center">No metrics returned.</p>
          </template>
          <Column field="name" header="Metric" />
          <Column field="value" header="Value">
            <template #body="{ data }">
              {{ data.value === null ? "null" : data.value }}
            </template>
          </Column>
          <Column field="unit" header="Unit" />
        </DataTable>

        <div class="issues-grid mt-3">
          <Panel header="Warnings">
            <p v-if="!calculationResult.warnings.length">None</p>
            <ul v-else>
              <li
                v-for="(warning, warningIndex) in calculationResult.warnings"
                :key="`${warning.code}-${warningIndex}`"
              >
                <code>{{ warning.code }}</code> {{ warning.message }}
              </li>
            </ul>
          </Panel>
          <Panel header="Hard Failures">
            <p v-if="!calculationResult.hardFailures.length">None</p>
            <ul v-else>
              <li
                v-for="(
                  failure, failureIndex
                ) in calculationResult.hardFailures"
                :key="`${failure.code}-${failureIndex}`"
              >
                <code>{{ failure.code }}</code> {{ failure.message }}
              </li>
            </ul>
          </Panel>
        </div>

        <Panel
          v-if="calculationResult.debugSummary"
          header="Debug Summary"
          class="mt-3"
          toggleable
          collapsed
        >
          <AdminJsonViewer :value="calculationResult.debugSummary" />
        </Panel>

        <div class="artifact-actions mt-3">
          <Button
            label="Copy Result JSON"
            class="p-button-text"
            @click="copyJson(calculationResult)"
          />
        </div>
      </template>
    </Card>

    <Card class="mt-4">
      <template #title>Trace Lookup</template>
      <template #content>
        <div class="form-grid">
          <label>
            Trace ID
            <InputText v-model="traceId" placeholder="calculation trace uuid" />
            <small>
              Trace IDs are returned by every calculation run and shown in the
              parity, candidates, and ranking tools.
            </small>
          </label>
        </div>
        <Button
          label="Load Trace"
          class="p-button-success mt-3"
          :loading="isLoadingTrace"
          :disabled="!traceId"
          @click="loadTrace()"
        />
      </template>
    </Card>

    <div v-if="traceError" class="error-banner">{{ traceError }}</div>

    <Panel v-if="trace" header="Calculation Trace" class="mt-4">
      <div class="artifact-actions">
        <Button
          label="Copy Trace JSON"
          class="p-button-text"
          @click="copyJson(trace)"
        />
      </div>
      <AdminJsonViewer :value="trace" />
    </Panel>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";

import {
  fetchSizingReportContext,
  fetchSizingTrace,
  runSizingCalculationAction,
  type RunSizingCalculationActionInput
} from "@/libs/aiDos/adminApi";
import {
  copyJsonToClipboard,
  copyTextToClipboard,
  errorMessage,
  statusSeverity
} from "@/libs/aiDos/adminShared";
import type {
  RunSizingCalculationResponse,
  ScenarioContext
} from "@/serverSizing/types";
import AdminJsonViewer from "./AdminJsonViewer.vue";

const toast = useToast();
const calculationModes: Array<
  RunSizingCalculationActionInput["calculationMode"]
> = ["baseline_existing_equipment", "parity_fixture"];
const scenarioId = ref("");
const calculationMode = ref<RunSizingCalculationActionInput["calculationMode"]>(
  "baseline_existing_equipment"
);
const debug = ref(true);
const persistTrace = ref(true);
const isCalculating = ref(false);
const isInspecting = ref(false);
const isLoadingTrace = ref(false);
const error = ref<string | null>(null);
const traceError = ref<string | null>(null);
const calculationResult = ref<RunSizingCalculationResponse | null>(null);
const scenarioContext = ref<ScenarioContext | null>(null);
const traceId = ref("");
const trace = ref<Record<string, unknown> | null>(null);

const recordCount = (value: unknown) =>
  value && typeof value === "object" ? Object.keys(value).length : 0;

const contextSummary = computed(() => {
  if (!scenarioContext.value) return null;
  const context = scenarioContext.value;
  return {
    counts: [
      { label: "Pumps", count: recordCount(context.pumps), required: true },
      { label: "Cables", count: recordCount(context.cables), required: true },
      {
        label: "Motor",
        count: context.equipment?.r_motor ? 1 : 0,
        required: true
      },
      {
        label: "Intakes",
        count: recordCount(context.intakes),
        required: false
      },
      { label: "Seals", count: recordCount(context.seals), required: false },
      {
        label: "Housings",
        count: recordCount(context.housings),
        required: false
      },
      {
        label: "Deviation Survey",
        count: context.deviationSurvey ? 1 : 0,
        required: false
      }
    ],
    missingData: context.missingData || []
  };
});

const metricRows = computed(() =>
  Object.entries(calculationResult.value?.metrics || {}).map(
    ([name, metric]) => ({
      name,
      value: metric.value,
      unit: metric.unit
    })
  )
);

const inspectContext = async () => {
  isInspecting.value = true;
  error.value = null;
  scenarioContext.value = null;
  try {
    scenarioContext.value = await fetchSizingReportContext(
      scenarioId.value.trim()
    );
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    isInspecting.value = false;
  }
};

const runCalculation = async () => {
  isCalculating.value = true;
  error.value = null;
  calculationResult.value = null;
  try {
    calculationResult.value = await runSizingCalculationAction({
      scenarioId: scenarioId.value.trim(),
      calculationMode: calculationMode.value,
      debug: debug.value,
      persistTrace: persistTrace.value
    });
    if (calculationResult.value?.traceId && persistTrace.value) {
      traceId.value = calculationResult.value.traceId;
    }
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    isCalculating.value = false;
  }
};

const loadTrace = async (requestedTraceId?: string) => {
  const lookupId = (requestedTraceId || traceId.value).trim();
  if (!lookupId) return;
  traceId.value = lookupId;
  isLoadingTrace.value = true;
  traceError.value = null;
  trace.value = null;
  try {
    trace.value = await fetchSizingTrace(lookupId);
  } catch (err) {
    traceError.value = errorMessage(err);
  } finally {
    isLoadingTrace.value = false;
  }
};

const copyJson = (value: unknown) => copyJsonToClipboard(toast, value);
const copyText = (value: string) => copyTextToClipboard(toast, value);
</script>

<style scoped lang="scss">
.page-header {
  max-width: 58rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.form-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 700;

  small {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
    font-weight: 400;
    line-height: 1.35;
  }
}

.toggle-label {
  align-items: flex-start;
}

.action-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.5rem;
  color: var(--red-700);
  margin: 1rem 0;
  padding: 1rem;
}

.muted-copy {
  color: var(--text-color-secondary);
  margin: 0;
}

.context-grid {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.context-chip {
  border: 1px solid var(--surface-400);
  border-radius: 999px;
  color: var(--text-color);
  display: inline-flex;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 0.2rem 0.5rem;

  &.empty {
    background: var(--red-50);
    border-color: var(--red-200);
    color: var(--red-700);
  }
}

.provenance-grid {
  display: grid;
  gap: 0.5rem;

  .hash {
    word-break: break-all;
  }
}

.issues-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
}

.artifact-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.json-output {
  background: var(--surface-900);
  border: 1px solid var(--surface-700);
  border-radius: 0.5rem;
  color: var(--surface-0);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.45;
  max-height: 28rem;
  overflow: auto;
  padding: 1rem;
  white-space: pre;
}
</style>
